import { useMemo } from 'react';
import { nip19 } from 'nostr-tools';
import type { NostrMetadata } from '@nostrify/nostrify';
import { genUserName } from '@/lib/genUserName';

interface ProfileHeaderProps {
  pubkey: string;
  metadata?: NostrMetadata;
  postCount?: number;
  isLoading?: boolean;
}

export function ProfileHeader({ pubkey, metadata, postCount, isLoading }: ProfileHeaderProps) {
  const displayName = metadata?.display_name ?? metadata?.name ?? genUserName(pubkey);
  const about = metadata?.about?.trim();
  const nip05 = metadata?.nip05;
  const website = metadata?.website;
  const lud16 = metadata?.lud16;
  const picture = metadata?.picture;
  const banner = metadata?.banner;

  const npub = useMemo(() => {
    try {
      return nip19.npubEncode(pubkey);
    } catch {
      return '';
    }
  }, [pubkey]);

  return (
    <header className="relative pixel-border bg-card/80 backdrop-blur-sm overflow-hidden">
      {/* Banner slot */}
      <div className="relative h-40 md:h-56 overflow-hidden bg-gradient-to-br from-[hsl(318_80%_30%)] via-[hsl(265_60%_18%)] to-[hsl(185_80%_20%)]">
        {banner && (
          <img
            src={banner}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-70 mix-blend-screen"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        )}
        {/* Vaporwave floor grid */}
        <div className="retro-floor opacity-60" />
        {/* Sun circle */}
        <div
          aria-hidden
          className="absolute left-1/2 -translate-x-1/2 bottom-[40%] w-40 h-40 rounded-full"
          style={{
            background:
              'radial-gradient(circle, hsl(318 100% 70%) 0%, hsl(318 100% 50%) 45%, transparent 70%)',
            boxShadow: '0 0 50px hsl(318 100% 58% / 0.8)',
          }}
        />
        {/* Scan overlay text */}
        <div className="absolute top-2 left-3 font-pixel text-[10px] text-neon-green tracking-widest flex items-center gap-2">
          <span className="animate-star-twinkle">●</span>
          <span className="text-glow-green">SYSTEM ONLINE</span>
        </div>
        <div className="absolute top-2 right-3 font-pixel text-[10px] text-neon-cyan tracking-widest text-glow-cyan">
          PLAYER_01
        </div>
      </div>

      {/* Body */}
      <div className="relative px-4 md:px-6 pb-5">
        {/* Avatar — pixel frame overlapping banner */}
        <div className="flex items-end gap-4 -mt-10 md:-mt-14">
          <div className="shrink-0 pixel-border-cyan bg-black p-1">
            {picture ? (
              <img
                src={picture}
                alt=""
                className="block w-20 h-20 md:w-24 md:h-24 object-cover bg-black"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            ) : (
              <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center bg-gradient-to-br from-primary to-accent font-pixel text-3xl text-primary-foreground">
                {displayName[0]?.toUpperCase() ?? '?'}
              </div>
            )}
          </div>

          {/* Stats pill */}
          <div className="flex-1 pb-1 flex flex-wrap gap-2 justify-end">
            <StatBlock label="POSTS" value={isLoading ? '—' : String(postCount ?? 0).padStart(3, '0')} color="magenta" />
            <StatBlock label="LV" value="99" color="cyan" />
            <StatBlock label="HP" value="100" color="green" />
          </div>
        </div>

        {/* Name + identity */}
        <div className="mt-4 space-y-1">
          <h1 className="font-pixel text-xl md:text-2xl text-neon-magenta text-glow-magenta break-words">
            {displayName}
          </h1>
          {nip05 && (
            <div className="font-mono text-xs md:text-sm text-neon-cyan text-glow-cyan">
              &lt;{nip05}&gt;
            </div>
          )}
          {npub && (
            <div className="font-mono text-[10px] md:text-xs text-muted-foreground break-all">
              <span className="text-neon-green">$</span> {npub}
            </div>
          )}
        </div>

        {/* About */}
        {about && (
          <div className="mt-4 font-mono text-sm md:text-[15px] text-foreground/90 whitespace-pre-wrap break-words border-l-2 border-neon-amber pl-3 py-1 bg-black/30">
            {about}
          </div>
        )}

        {/* Meta row */}
        {(website || lud16) && (
          <div className="mt-4 flex flex-wrap gap-3 font-pixel text-[10px] tracking-widest">
            {website && (
              <a
                href={website.startsWith('http') ? website : `https://${website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="pixel-border-soft px-2 py-1 bg-black/60 text-neon-cyan hover:text-glow-cyan"
              >
                [WWW]&nbsp;{truncate(website.replace(/^https?:\/\//, ''), 32)}
              </a>
            )}
            {lud16 && (
              <span className="pixel-border-soft px-2 py-1 bg-black/60 text-neon-amber">
                [⚡]&nbsp;{truncate(lud16, 32)}
              </span>
            )}
          </div>
        )}
      </div>
    </header>
  );
}

function StatBlock({ label, value, color }: { label: string; value: string; color: 'magenta' | 'cyan' | 'green' }) {
  const valueClass =
    color === 'magenta' ? 'text-neon-magenta text-glow-magenta' :
    color === 'cyan' ? 'text-neon-cyan text-glow-cyan' :
    'text-neon-green text-glow-green';
  return (
    <div className="pixel-border-soft bg-black/80 px-3 py-1 min-w-[64px] text-center">
      <div className="font-pixel text-[9px] text-muted-foreground tracking-widest">{label}</div>
      <div className={`font-pixel text-base leading-tight ${valueClass}`}>{value}</div>
    </div>
  );
}

function truncate(s: string, n: number): string {
  return s.length <= n ? s : s.slice(0, n - 1) + '…';
}
